import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { AgGridReact } from "ag-grid-react";
import { matchUserProjectRole } from "utils/utils";
import { useIntegrationProjectParticipants } from "hooks/project-participants";
import { ProjectContext } from "context/ProjectProvider";
import { ColDef, ValueGetterParams } from "ag-grid-enterprise";
import { IdLinkComponentNewModel } from "components/cell-renders";
import CiqAgSearch from "components/ciq-ag-search";
import CiqLogPageHeader, {
  calculateLogPageStats
} from "components/ciq-log-page-header";
import useFeatureSubmittalLogData from "pages/submittal-log/hooks/submittal-log-data";
import { submittalSequenceComparator } from "pages/submittals-log-page/utils";
import {
  coldefNavigation,
  coldefHiddenField,
  coldefSingleField
} from "entity-app/shared-components/log-render-components/col-defs";
import {
  submittalStatusCellRendererV2,
  twoRowCellRenderer,
  twoRowGetQuickFilterText
} from "entity-app/shared-components/log-render-components/cell-renders";
import { SelectCellEditorV2 } from "components/cell-editor/select-cell-editor";
import { EUserTypes, SubmittalStatusToStrMap } from "../../../constants";

function PartnerSubmittalLog() {
  const { projectId } = useParams() as any;
  const gridRef = useRef<AgGridReact<any>>(null);

  const {
    tokenContents,
    columnHeaders: { submittalHeaderMap },
    projectDetails,
    tokenRetrievalState
  } = useContext(ProjectContext);

  const {
    submittalList: submittalListData,
    submittalTypesMap: typesMap,
    statusesMap,
    WFProjectParticipants,
    isIntegrationMode
  } = useFeatureSubmittalLogData(projectId, tokenRetrievalState?.token);

  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();

  const [isGridReady, setGridReady] = useState(false);

  const isLoggedInUserGC = matchUserProjectRole(
    EUserTypes.GENERAL_CONTRACTOR,
    tokenContents?.role
  );

  const { data: INTGProjectParticipants } = useIntegrationProjectParticipants(
    !isIntegrationMode || !isLoggedInUserGC
  );

  const projectParticipants = isIntegrationMode
    ? INTGProjectParticipants
    : WFProjectParticipants;

  useEffect(() => {
    function handleResize() {
      if (isGridReady && gridRef && gridRef.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [isGridReady]);

  const columnDefs = useMemo<ColDef[]>(() => {
    return [
      coldefNavigation(
        {
          field: "sequence_id",
          colId: "submittal_sequence_id",
          headerName: submittalHeaderMap.submittal_id,
          pinned: "left",
          lockPosition: "left",
          initialWidth: 80,
          lockVisible: true,
          filter: false,
          sortable: true,
          menuTabs: [],
          editable: false,
          cellRendererParams: {
            projectId
          },
          cellRenderer: IdLinkComponentNewModel,
          comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any) =>
            submittalSequenceComparator(
              valueA,
              valueB,
              nodeA,
              nodeB,
              projectDetails?.spec_section
            )
        },
        projectId,
        "submittals"
      ),
      coldefSingleField({
        field: "title",
        colId: "title",
        headerName: submittalHeaderMap.title,
        filter: false,
        sortable: true,
        menuTabs: [],
        initialWidth: 180,
        pinned: "left",
        lockPosition: "left",
        cellRenderer: twoRowCellRenderer,
        cellRendererParams: {
          featureKey: "submittals",
          projectId
        },
        getQuickFilterText: twoRowGetQuickFilterText,
        lockVisible: true,

        comparator(valueA: any, valueB: any, nodeA: any, nodeB: any) {
          const compareA = nodeA.data.title ?? nodeA.data.description;
          const compareB = nodeB.data.title ?? nodeB.data.description;
          return compareA.toLowerCase().localeCompare(compareB.toLowerCase());
        },
        editable: false
      }),
      coldefHiddenField({
        field: "description",
        headerName: "Description",
        pinned: "left",
        lockPosition: "left"
      }),
      coldefSingleField({
        field: "revision",
        colId: "revision",
        headerName: submittalHeaderMap?.revision || "",
        headerTooltip: submittalHeaderMap?.revision || "",
        tooltipField: undefined,
        editable: false,
        menuTabs: ["filterMenuTab"],
        initialWidth: 150,
        minWidth: 100,
        lockVisible: true
      }),
      coldefSingleField({
        field: "type.value",
        colId: "type",
        headerName: submittalHeaderMap?.type || "",
        headerTooltip: submittalHeaderMap?.type || "",
        tooltipField: undefined,
        editable: false,
        menuTabs: ["filterMenuTab"],
        initialWidth: 150,
        minWidth: 100,
        cellEditor: SelectCellEditorV2,
        lockVisible: true,
        cellEditorParams: {
          type: "regular-select",
          dataMapVar: "typesMap",
          valueVar: "id",
          labelVar: "value",
          showSearch: true
        }
      }),
      coldefSingleField({
        field: "workflow_status",
        colId: "status",
        headerName: submittalHeaderMap?.workflow_status,
        lockVisible: true,
        initialWidth: 200,
        minWidth: 200,
        tooltipField: undefined,
        tooltipValueGetter: undefined,
        cellRenderer: submittalStatusCellRendererV2,
        valueGetter: (params: ValueGetterParams) => {
          return SubmittalStatusToStrMap[params.data.workflow_status.id];
        }
      })
    ];
  }, [projectDetails?.spec_section, projectId, submittalHeaderMap]);

  const defaultColDef: {} = useMemo(() => {
    return {
      sortable: false,
      editable: false,
      filter: false,
      resizable: true,
      menuTabs: []
    };
  }, []);

  const onFiltersApplied = () => {
    setStatsData(
      calculateLogPageStats({
        gridRef
      })
    );
  };

  const headerItems = [
    <CiqAgSearch
      key="Submittals_search"
      gridRef={gridRef}
      placeholder="Search Submittals"
    />
  ];

  return (
    <div className="grow flex flex-col">
      <CiqLogPageHeader
        {...{
          entityName: "Submittals",
          titleParam: {
            title: "Submittals",
            totalCount: submittalListData?.length,
            filterStats: statsData
          },
          gridRef,
          items: headerItems
        }}
      />
      <div className="grow bg-white ag-theme-alpine">
        <AgGridReact<any>
          tooltipMouseTrack
          ref={gridRef}
          rowData={submittalListData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          headerHeight={36}
          suppressAggFuncInHeader
          suppressDragLeaveHidesColumns
          readOnlyEdit
          animateRows={false}
          debounceVerticalScrollbar
          suppressMovableColumns
          onGridReady={() => {
            setGridReady(true);
            gridRef.current?.api?.sizeColumnsToFit();
          }}
          rowHeight={62}
          context={{
            projectDetails,
            typesMap,
            statusesMap,
            isIntegrationMode,
            projectParticipants
          }}
          suppressContextMenu
          overlayNoRowsTemplate="Import a log or click New submittal to create a log entry."
          onFilterChanged={onFiltersApplied}
        />
      </div>
    </div>
  );
}

export default PartnerSubmittalLog;
